import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { getCurrencySymbolByCurrency } from 'utils';

import { PriceBlock } from 'fronton-react';
import {
  getPrimaryPrice,
  getProductPriceCategory,
  getSecondPrice,
  isWebSellable,
  getPrimaryPriceUnitSlug,
  getAdditionalPriceUnitSlug,
  getAdditionalPriceUnitName,
  isLaminateCalculator,
  getPreviousAdditionalPrice,
  isOutOfStockPriceDisplayed,
} from 'features/product-info/ducks/selectors';
import { getLaminateConfigurationArea } from 'features/configurations/ducks/selectors';
import { getGoodsSupplyCase } from 'features/goods-supply/selectors';
import { UOMS } from 'features/product-info/constants';
import { RootReducer } from 'store/root-reducer';
import { PriceMainProps } from 'common/components/product-price-view/types';
import { getIsSetPath } from 'common/selectors';
import { PriceViewMappedState, PriceViewProps } from './types';
import { calculatePriceType, concatUnit, getProjectPriceResult, isValues, ratioToPercent } from './utils';
import { PdpProjectBoxCount } from '../pdp-project-box-count';

const BEST_PRICE_TYPE = 'bestPrice';
const BLOCKED_SUPPLY_CASES = ['CASE_1', 'CASE_5'] as const;

export const PriceView: React.FC<
  PriceViewProps & {
    isStickyPanel: boolean;
    secondPriceClassName?: string;
    inBasket: boolean;
    quantity: number;
  }
> = ({
  firstPrice,
  secondPrice,
  isSellable,
  supplyCase,
  productPriceCategory,
  isStickyPanel,
  mainUom,
  additionalUom,
  additionalUomRus,
  configurationArea,
  isLaminateCalc,
  previousAdditionalPrice,
  inBasket,
  quantity,
  outOfStockPriceDisplayed,
  isSetPath,
  ...htmlProps
}) => {
  const currency = getCurrencySymbolByCurrency(firstPrice.currency);
  const isBestPrice = productPriceCategory === BEST_PRICE_TYPE;
  const type = calculatePriceType(isBestPrice, firstPrice.prevPrice, outOfStockPriceDisplayed);

  const isCorrectUomForLaminateProjectPrice =
    (mainUom === UOMS.CAR && additionalUom === UOMS.M2) ||
    (mainUom === UOMS.M2 && additionalUom === UOMS.CAR);

  const isShowProjectPrice =
    Boolean(configurationArea) && isCorrectUomForLaminateProjectPrice && isLaminateCalc;

  const { projectPrice, areaPerBox, amountBox } = useMemo(
    () =>
      getProjectPriceResult({
        inBasket,
        quantity,
        mainUom,
        configurationArea,
        previousAdditionalPrice,
        firstPrice,
        secondPrice,
      }),
    [configurationArea, firstPrice, inBasket, mainUom, previousAdditionalPrice, quantity, secondPrice],
  );

  const renderPrice = (price: number, prevPrice?: string | null, discountPercent?: number | null) => (
    <PriceBlock
      price={price}
      units={isSetPath ? undefined : concatUnit(firstPrice)}
      oldPrice={prevPrice ? Number(prevPrice) : undefined}
      oldUnits={currency}
      unitPrice={isValues(secondPrice) ? secondPrice.price : undefined}
      unitUnits={isValues(secondPrice) ? concatUnit(secondPrice) : undefined}
      discount={ratioToPercent(discountPercent)}
      type={type}
      size={isStickyPanel ? 'm' : 'xl'}
      {...htmlProps}
    />
  );

  const shouldHidePrice = (): boolean => {
    if (!isSellable) return false;
    const isBlocked =
      supplyCase !== null &&
      BLOCKED_SUPPLY_CASES.includes(supplyCase as (typeof BLOCKED_SUPPLY_CASES)[number]);
    return isBlocked && !outOfStockPriceDisplayed;
  };

  const shouldShowProjectPriceCondition = isSellable && !outOfStockPriceDisplayed && isShowProjectPrice;

  const getPriceContent = (): React.ReactNode => {
    if (shouldHidePrice()) {
      return null;
    }

    if (shouldShowProjectPriceCondition) {
      return (
        <>
          {renderPrice(projectPrice.price, projectPrice.prevPrice, projectPrice.discountPercent)}
          <PdpProjectBoxCount
            additionalUomRus={additionalUomRus}
            mainUnit={firstPrice.unit}
            areaPerBox={areaPerBox}
            amountBox={amountBox}
          />
        </>
      );
    }

    return renderPrice(firstPrice.price, firstPrice.prevPrice, firstPrice.discountPercent);
  };

  const content = getPriceContent();

  return <>{content}</>;
};

export const mapStoreToProps = (store: RootReducer): PriceViewMappedState => {
  const firstPrice = getPrimaryPrice(store) as PriceMainProps;
  const secondPrice = getSecondPrice(store) as PriceMainProps;
  const isSellable = isWebSellable(store);
  const supplyCase = getGoodsSupplyCase(store);
  const productPriceCategory = getProductPriceCategory(store) ?? '';
  const mainUom = getPrimaryPriceUnitSlug(store);
  const additionalUom = getAdditionalPriceUnitSlug(store);
  const additionalUomRus = getAdditionalPriceUnitName(store);
  const configurationArea = getLaminateConfigurationArea(store);
  const isLaminateCalc = isLaminateCalculator(store);
  const previousAdditionalPrice = getPreviousAdditionalPrice(store);
  const outOfStockPriceDisplayed = isOutOfStockPriceDisplayed(store);
  const isSetPath = getIsSetPath(store);

  return {
    firstPrice,
    secondPrice,
    isSellable,
    supplyCase,
    productPriceCategory,
    mainUom,
    additionalUom,
    additionalUomRus,
    configurationArea,
    isLaminateCalc,
    previousAdditionalPrice,
    outOfStockPriceDisplayed,
    isSetPath,
  };
};

export const PdpPriceView = connect(mapStoreToProps, null, (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
}))(PriceView);
